import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, useTheme, Link, Checkbox, FormControlLabel, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AccountBalance as BankingIcon } from '@mui/icons-material';
import api from 'config/axios';

interface InfoProps {
  type: string;
  btnTxt?: string;
  btnColor?: string;
  btnIcon?: any;
  icon?: any;
  iconBg?: string;
  description: string;
  customStyles?: any;
  addBtn?: string;
  addBtnIcon?: any;
  addBtnColor?: string;
  btnRoute?: string;
}

const Info: React.FC<InfoProps> = ({
  type,
  btnTxt,
  icon,
  description,
  btnColor,
  iconBg,
  btnIcon,
  customStyles,
  addBtn,
  addBtnIcon,
  addBtnColor,
  btnRoute,
}) => {
  const theme = useTheme();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsApiAccepted, setTermsApiAccepted] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);

  const iconColor = theme.palette.primary.main;
  const circleBg = '#F2F4F8';
  const navigate = useNavigate();

  const titleMap: Record<string, string> = {
    awaitingAccountOpening: 'Abra a sua Conta Digital',
    userNotEligible: 'Estamos revisando suas informações.',
    success: 'Cadastro concluído com sucesso!',
    awaitingKyc: 'Tudo certo por aqui!',
    kycSuccess: 'Confirmamos a sua identidade!',
    kycError: 'Não conseguimos confirmar a<br />sua identidade.',
  };

  const title = titleMap[type];

  useEffect(() => {
    let isMounted = true;

    const fetchTermsAcceptance = async () => {
      if (type !== 'awaitingAccountOpening') return;
      setTermsLoading(true);
      try {
        const { data } = await api.get('/terms-acceptance');
        const accepted = Boolean(data?.accepted);
        if (!isMounted) return;
        setTermsApiAccepted(accepted);
        setTermsAccepted(accepted);
        if (accepted && btnRoute) {
          navigate(btnRoute);
        }
      } catch (error: any) {
        if (!isMounted) return;
        setTermsApiAccepted(false);
        setTermsAccepted(false);
      } finally {
        setTermsLoading(false);
      }
    };

    fetchTermsAcceptance();

    if (type !== 'awaitingAccountOpening') {
      setTermsAccepted(false);
      setTermsApiAccepted(false);
      setTermsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [type]);

  const handleTermsChange = (checked: boolean) => {
    setTermsAccepted(checked);
  };

  const handleBtnClick = async () => {
    if (type === 'awaitingAccountOpening' && !termsApiAccepted && termsAccepted) {
      setTermsLoading(true);
      try {
        await api.post('/terms-acceptance');
        setTermsApiAccepted(true);
      } catch (error: any) {
        // Optionally handle error
      } finally {
        setTermsLoading(false);
      }
    }
    if (btnRoute) navigate(btnRoute);
  };

  if (type === 'awaitingAccountOpening' && termsLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={250}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      padding="30px"
      sx={{ textAlign: 'center', boxShadow: '2px 2px 10px 0px #0000000D' }}
    >
      <Box
        sx={{
          p: 1.5,
          bgcolor: iconBg || circleBg,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 64,
          height: 64,
          mb: 3,
        }}
      >
        {icon || <BankingIcon sx={{ color: iconColor, fontSize: 32 }} />}
      </Box>

      <Typography variant="h2" fontWeight={600} lineHeight={1.3} mb={2} dangerouslySetInnerHTML={{ __html: title }} />

      <Typography variant="subtitle2" sx={{ color: '#5A5959', mb: 5, ...customStyles }} dangerouslySetInnerHTML={{ __html: description }} />

      {type === 'awaitingAccountOpening' && (
        <Box
          sx={{
            color: '#5A5959',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            textAlign: 'left',
            mb: '5vh',
          }}
        >
          <Typography component="div" sx={{ fontSize: '16px', fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}>
            Para abrir a sua Conta Digital é muito fácil:
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography component="div" sx={{ fontSize: '16px', fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}>
              1. Complete os dados da empresa.
            </Typography>
            <Typography component="div" sx={{ fontSize: '16px', fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}>
              2. Complete os dados do responsável legal.
            </Typography>
            <Typography component="div" sx={{ fontSize: '16px', fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}>
              3. Vamos enviar um link para o responsável legal realizar a biometria.
            </Typography>
            <Typography component="div" sx={{ fontSize: '16px', fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}>
              4. Pronto! Sua conta já está aberta e pronta para movimentar.
            </Typography>

            <Box
              sx={{
                mt: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 2,
              }}
            >
              <Typography component="div" sx={{ fontSize: '16px', fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}>
                Para seguir com a abertura da Conta Digital você precisa
                <br />
                ler e aceitar os{' '}
                <Link
                  href="https://torqi-public.s3.us-east-1.amazonaws.com/Termos+e+Condi%C3%A7%C3%B5es+de+Uso+da+Conta+Digital.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    fontWeight: 500,
                  }}
                >
                  Termos e Condições de Uso da Conta Digital
                </Link>
              </Typography>

              <FormControlLabel
                sx={{ m: 0 }}
                control={<Checkbox checked={termsAccepted} disabled={termsLoading} onChange={(e) => handleTermsChange(e.target.checked)} />}
                label={
                  <Typography
                    component="span"
                    sx={{ fontSize: '12px', fontFamily: 'Poppins, sans-serif', fontWeight: 400, color: '#5A5959' }}
                  >
                    Declaro que li e estou de acordo com os Termos e Condições de Uso da Conta Digital
                  </Typography>
                }
              />
            </Box>
          </Box>
        </Box>
      )}

      {btnTxt && (
        <Button
          variant="contained"
          size="large"
          disabled={type === 'awaitingAccountOpening' && (!termsAccepted || termsLoading)}
          sx={{
            backgroundColor: btnColor || iconColor,
            ':hover': { backgroundColor: btnColor || iconColor },
            boxShadow: 'none',
            textTransform: 'none',
          }}
          onClick={handleBtnClick}
        >
          <Typography variant="button" sx={{ textTransform: 'none' }} dangerouslySetInnerHTML={{ __html: btnTxt }} />
          {btnIcon && <> {btnIcon} </>}
        </Button>
      )}

      {addBtn && (
        <Button
          variant="contained"
          size="large"
          sx={{
            backgroundColor: addBtnColor || iconColor,
            ':hover': { backgroundColor: addBtnColor || iconColor },
            boxShadow: 'none',
            textTransform: 'none',
            mt: 5,
          }}
        >
          <Typography variant="button" sx={{ textTransform: 'none' }} dangerouslySetInnerHTML={{ __html: addBtn }} />
          {addBtnIcon && <> {addBtnIcon} </>}
        </Button>
      )}
    </Box>
  );
};

export default Info;
